import { Component, OnDestroy, ViewChild, inject, AfterViewInit, ChangeDetectorRef, OnInit } from '@angular/core';
import { NetzbetreiberService } from '../../service/netzbetreiber/netzbetreiber.service';
import { Netzbetreiber } from '../../domain/model/netzbetreiber';
import { NetzbetreiberDataSource } from './netzbetreiber-data-source';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { RoutingService } from '../../service/common/routing/routing.service';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl } from '@angular/forms';
import { debounceTime, filter, map, shareReplay, startWith, takeWhile, tap } from 'rxjs/operators';
import { NetzbetreiberExportService } from '../../service/netzbetreiber/netzbetreiber-export.service';
import { Kundenbetreuer } from '../../domain/model/kundenbetreuer';
import { Schaltleitung } from '../../domain/model/schaltleitung';
import { combineLatest, distinctUntilChanged, merge, Observable, switchMap } from 'rxjs';
import { Pageable } from '../../common/paging/pageable';
import { ReplaySubject } from 'rxjs/internal/ReplaySubject';
import { Subject } from 'rxjs/internal/Subject';
import { distinctUntilFilterChanged } from '../../common/util/helper';

const FILTER_DEBOUNCE_TIME = 250;

@Component({
    selector: 'tnb-netzbetreiber-list',
    templateUrl: './netzbetreiber-list.component.html',
    styleUrls: [
        './netzbetreiber-list.component.scss',
    ],
    standalone: false,
})
export class NetzbetreiberListComponent implements OnInit, AfterViewInit, OnDestroy {

    public readonly PAGE_SIZE = 50;

    routingService = inject(RoutingService);
    private netzbetreiberService = inject(NetzbetreiberService);
    private netzbetreiberExportService = inject(NetzbetreiberExportService);
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private routeService = inject(RoutingService);
    private changeDetectorRef = inject(ChangeDetectorRef);

    private static DUE_THRESHOLD_MS = 1000 * 60 * 60 * 24 * 25; // older than 25 days
    private static OVERDUE_THRESHOLD_MS = 1000 * 60 * 60 * 24 * 30;  // older than 30 days

    public netzbetreiberList: NetzbetreiberDataSource = new NetzbetreiberDataSource();
    public page$: Observable<Pageable>;
    public searchCtrl = new FormControl('');

    @ViewChild(MatSort, { static: false }) sort: MatSort;
    @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;

    private pageFromUserInteraction$: Observable<Pageable>;
    private pageEventFromUserInteraction$ = new ReplaySubject<PageEvent>(1);
    private sortFromUserInteraction$ = new ReplaySubject<Sort>(1);
    private filterFromUserInteraction$ = new Subject<string>();
    private filterFromRouteChanges$ = new Subject<string>();

    private netzbetreiber: Netzbetreiber[] = [];
    private currentPageIndex = 0;
    private componentDestroyed = false;

    public columnsToDisplay = [
        'name',
        'teilgebiet',
        'netzbetreibernummer',
        'stadtwerkeId',
        'schaltleitungen',
        'kundenbetreuerList',
        'lastChanged',
        'kaskadenKontakt.emailAlarmierungKaskade',
        'kaskadenKontakt.telefonAlarmierungKaskade',
        'kaskadenKontakt.emailInformationKaskade',
        'kaskadenKontakt.telefonBereitschaft',
        'dienstleistungsartTitle',
    ];

    constructor() {
        this.setupFilterObservable();
        this.setupPageObservable();
        this.setupReloadObservable();
    }

    ngOnInit(): void {
        this.searchCtrl.valueChanges
            .pipe(
                debounceTime(FILTER_DEBOUNCE_TIME),
                takeWhile(() => !this.componentDestroyed),
            )
            .subscribe(v => this.filterFromUserInteraction$.next(v));

        this.route.queryParams
            .pipe(
                map(params => params['filter'] || ''),
                tap(f => this.searchCtrl.setValue(f, { emitEvent: false })),
                takeWhile(() => !this.componentDestroyed),
            )
            .subscribe(v => this.filterFromRouteChanges$.next(v));
    }

    ngAfterViewInit(): void {
        this.netzbetreiberList.sort = this.sort;
        this.netzbetreiberList.paginator = this.paginator;
    }

    ngOnDestroy(): void {
        this.componentDestroyed = true;
    }

    navigateToDetails(netzbetreiber: Netzbetreiber): void {
        this.router.navigate([
            this.routingService.netzbetreiberPath(),
        ], {
            queryParams: { stadtwerkeId: netzbetreiber.stadtwerkeId, openedInSideNav: true },
            queryParamsHandling: 'merge',
        });
    }

    private setupPageObservable() {
        this.pageFromUserInteraction$ = combineLatest([
            this.sortFromUserInteraction$,
            this.pageEventFromUserInteraction$,
        ])
            .pipe(
                map(params => {
                    const pageable = new Pageable();

                    const sort = params[0];
                    const page = params[1];

                    pageable.sort = sort?.active || '';
                    pageable.direction = sort?.direction || '';
                    pageable.page = page?.pageIndex || 0;

                    return pageable;
                }),
                shareReplay(1),
            );

        this.route.queryParams
            .pipe(
                takeWhile(() => !this.componentDestroyed),
            )
            .subscribe(params => {
                this.sortFromUserInteraction$.next({
                    active: params['sort'],
                    direction: params['direction'],
                });
                this.pageEventFromUserInteraction$.next({
                    pageIndex: +params['page'],
                } as PageEvent);
            });

        this.page$ = this.pageFromUserInteraction$
            .pipe(
                distinctUntilFilterChanged(),
                tap(page => {
                    this.currentPageIndex = page.page;
                    this.netzbetreiberList.data = [];  // Clear first
                    this.changeDetectorRef.markForCheck();
                    setTimeout(() => {
                        this.netzbetreiberList.data = this.netzbetreiber;
                        this.changeDetectorRef.markForCheck();
                    });
                }),
                shareReplay(1),
            );
    }

    private setupReloadObservable(): void {
        // Goto first page, when filter changes
        this.filterFromUserInteraction$
            .pipe(
                takeWhile(() => !this.componentDestroyed),
            )
            .subscribe(_filter => {
                const page = new PageEvent();
                page.pageIndex = 0;
                this.pageEventFromUserInteraction$.next(page);
            });

        combineLatest([
            this.pageFromUserInteraction$,
            this.filterFromUserInteraction$,
        ])
            .pipe(
                filter(() => {
                    const currentParams = this.route.snapshot.queryParams;
                    return !currentParams['openedInSideNav'];
                }),
                map(param => {
                    const pageable = param[0];
                    const nbFilter = param[1];
                    return this.routeService.netzbetreiberTable(pageable, nbFilter);
                }),
                distinctUntilChanged(),
                takeWhile(() => !this.componentDestroyed),
            )
            .subscribe(url => {
                this.router.navigateByUrl(url);
            });

        this.netzbetreiberService.refreshRequested$
            .pipe(
                startWith(null),
                takeWhile(() => !this.componentDestroyed),
                switchMap(() => this.netzbetreiberService.getAlleNetzbetreiber()),
                map(netzbetreiber => {
                    netzbetreiber.forEach(n => {
                        n.dueClass = this.resolveIdentifierForLastChanged(n);
                    });
                    return netzbetreiber;
                }),
            )
            .subscribe(netzbetreiber => {
                this.netzbetreiberList.paginator.pageIndex = this.currentPageIndex;
                this.netzbetreiberList.data = netzbetreiber;
                this.netzbetreiber = netzbetreiber;
                this.changeDetectorRef.markForCheck();
            });
    }

    private resolveIdentifierForLastChanged(netzbetreiber: Netzbetreiber) {
        const diffMs = new Date().getTime() - new Date(netzbetreiber.lastChanged).getTime();

        if (!netzbetreiber.erzeugungsAnlagenPflegeAktiv) {
            return 'notActive';
        } else {
            if (diffMs < NetzbetreiberListComponent.DUE_THRESHOLD_MS) {
                return 'ok';
            } else if (diffMs < NetzbetreiberListComponent.OVERDUE_THRESHOLD_MS) {
                return 'due';
            } else {
                return 'overdue';
            }
        }
    }

    private setupFilterObservable() {
        merge(
            this.filterFromUserInteraction$,
            this.filterFromRouteChanges$,
        ).pipe(
            distinctUntilFilterChanged(),
            takeWhile(() => !this.componentDestroyed),
        )
            .subscribe(nbFilter => {
                this.netzbetreiberList.filter = nbFilter;
                this.changeDetectorRef.markForCheck();
            });
    }

    pageChange($event: PageEvent) {
        this.pageEventFromUserInteraction$.next($event);
    }

    sortChange($event: Sort) {
        if ($event.direction === '') {
            $event.active = '';
        }
        this.sortFromUserInteraction$.next($event);
    }

    public exportNetzbetreiber() {
        this.netzbetreiberExportService.exportNetzbetreiber();
    }

    public flatten(kundenbetreuerList: Kundenbetreuer[]): string {
        return kundenbetreuerList.map(function (kb) {
            return kb.nachname + ', ' + kb.vorname;
        }).join('/');
    }

    flattenSchaltleitungen(schaltleitungen: Schaltleitung[]): string {
        return schaltleitungen.map(function (sl) {
            return sl.name;
        }).join(', ');
    }
}
